import React, { useEffect, useRef } from 'react';

import { Avatar, Button, Code, Loader, Modal, Stack, Text, Title, useMantineTheme } from '@mantine/core';
import { IconCheck, IconCircleX, IconCreditCard, IconRotate } from '@tabler/icons-react';
import useCancelStripeTerminalReaderPayment from 'hooks/invoice/useCancelStripeTerminalReaderPayment';
import usePollInvoice from 'hooks/invoice/usePollInvoice';
import useRetryStripeTerminalReaderPayment from 'hooks/invoice/useRetryStripeTerminalReaderPayment';
import { TapToPayPage, useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { useQueryClient } from 'react-query';
import notificationHandler from 'utils/notifications';

export interface TapToPayTrackingContext {
  page: TapToPayPage;
  athleteId: string;
  amount: number;
  itemCount: number;
  saveCard: boolean;
  billingStartDate?: string;
}

interface TapToPayModalProps {
  opened: boolean;
  invoiceId: string;
  isChargeNow: boolean;
  onClose: () => void;
  onChangePaymentMethod: () => void;
  trackingContext: TapToPayTrackingContext;
  successButtonLabel?: string;
}

const TapToPayModal = ({
  opened,
  invoiceId,
  isChargeNow,
  onClose,
  onChangePaymentMethod,
  trackingContext,
  successButtonLabel = 'Back to Retail',
}: TapToPayModalProps): JSX.Element => {
  const queryClient = useQueryClient();
  const theme = useMantineTheme();
  const { errorNotification } = notificationHandler();
  const {
    trackTapToPayPaymentSucceeded,
    trackTapToPayPaymentFailed,
    trackTapToPayCardSaved,
    trackTapToPayCardSaveFailed,
    trackTapToPayCancelled,
    trackTapToPayChangePaymentMethodClicked,
    trackTapToPayRetryClicked,
    trackTapToPayPollingTimedOut,
  } = useStaffTracking();

  const {
    data: invoiceData,
    isLoading: isInvoiceLoading,
    isFetching: isInvoiceFetching,
    refetch: refetchInvoice,
    resetPoll: resetInvoicePoll,
    isTimedOut: isInvoicePollTimedOut,
  } = usePollInvoice({
    invoiceId: invoiceId,
    isChargeNow: isChargeNow,
  });

  const cancelTerminalReaderPaymentMutation = useCancelStripeTerminalReaderPayment();
  const retryTerminalPaymentMutation = useRetryStripeTerminalReaderPayment();

  // Each terminal state should emit its Mixpanel event exactly once per attempt —
  // success/failure/timeout flags can re-evaluate as the invoice is polled, but
  // the event must not fire repeatedly. firedRef guards each transition; retry
  // resets the `failed` slot so a fresh failure can re-fire.
  const firedRef = useRef({ succeeded: false, failed: false, timedOut: false });
  const retryAttemptRef = useRef(0);

  const invalidateAthleteQueries = (): void => {
    queryClient.invalidateQueries(['athletePackages']);
    queryClient.invalidateQueries(['paymentMethods']);
    queryClient.invalidateQueries(['athletes', trackingContext.athleteId]);
    queryClient.invalidateQueries(['athleteStatuses', trackingContext.athleteId]);
  };

  const handleClose = (): void => {
    invalidateAthleteQueries();
    onClose();
  };

  const isSucceeded =
    invoiceData?.data.status === 'PAID' ||
    (invoiceData?.data.status === 'UPCOMING' && !!invoiceData?.data.paymentMethod);
  const isFailed =
    invoiceData?.data.status === 'PAYMENT FAILED' || invoiceData?.data.status === 'FAILED PAST DUE';

  const isWaitingForTapToPay =
    !isInvoicePollTimedOut &&
    !isSucceeded &&
    !isFailed &&
    (invoiceData?.data.status === 'UPCOMING' || invoiceData?.data.status === 'PAST DUE') &&
    !invoiceData?.data.paymentMethod;
  const isProcessing = !isInvoicePollTimedOut && invoiceData?.data.status === 'PENDING';
  const isTimedOutPending = isInvoicePollTimedOut && !isSucceeded && !isFailed;

  // Cancel can be invoked from three modal states. Map them to the
  // {waiting, failed, timed_out} taxonomy from the spec.
  const resolveCancelFromState = (): 'waiting' | 'failed' | 'timed_out' => {
    if (isTimedOutPending) return 'timed_out';
    if (isFailed) return 'failed';
    return 'waiting';
  };

  const handleCancelTransaction = (): void => {
    trackTapToPayCancelled({
      page: trackingContext.page,
      invoice_id: invoiceId,
      from_state: resolveCancelFromState(),
    });
    cancelTerminalReaderPaymentMutation.mutate(invoiceId, {
      onSuccess: () => {
        handleClose();
      },
      onError: error => {
        errorNotification(
          'Cancel Failed',
          error.response?.data.message ?? 'Something went wrong. Please try again.',
        );
      },
    });
  };

  const handleChangePaymentMethod = (): void => {
    trackTapToPayChangePaymentMethodClicked({
      page: trackingContext.page,
      invoice_id: invoiceId,
    });
    // Cancels the in-flight terminal payment server-side, then hands control back
    // to the parent which preserves cart contents (vs. handleCancelTransaction
    // which clears everything).
    cancelTerminalReaderPaymentMutation.mutate(invoiceId, {
      onSuccess: () => {
        // The server-side teardown ran, so the athlete's cached rows are stale here too
        // even though the staff member stays in the flow.
        invalidateAthleteQueries();
        onChangePaymentMethod();
      },
      onError: error => {
        errorNotification(
          'Cancel Failed',
          error.response?.data.message ?? 'Something went wrong. Please try again.',
        );
      },
    });
  };

  const handleRetryTransaction = (): void => {
    retryAttemptRef.current += 1;
    trackTapToPayRetryClicked({
      page: trackingContext.page,
      invoice_id: invoiceId,
      retry_attempt: retryAttemptRef.current,
    });
    // Allow the next failure (or success) to re-fire its event.
    firedRef.current.failed = false;
    firedRef.current.succeeded = false;
    firedRef.current.timedOut = false;
    retryTerminalPaymentMutation.mutate(invoiceId, {
      onSuccess: () => {
        refetchInvoice();
      },
      onError: error => {
        errorNotification(
          'Retry Failed',
          error.response?.data.message ?? 'Something went wrong. Please try again.',
        );
      },
    });
  };

  // Success — charge-now → payment_succeeded, save-card → card_saved.
  useEffect(() => {
    if (!isSucceeded || firedRef.current.succeeded) return;
    firedRef.current.succeeded = true;
    if (isChargeNow) {
      trackTapToPayPaymentSucceeded({
        page: trackingContext.page,
        invoice_id: invoiceId,
        athlete_id: trackingContext.athleteId,
        amount: trackingContext.amount,
        item_count: trackingContext.itemCount,
        save_card: trackingContext.saveCard,
      });
    } else {
      trackTapToPayCardSaved({
        page: trackingContext.page,
        invoice_id: invoiceId,
        athlete_id: trackingContext.athleteId,
      });
    }
  }, [
    isSucceeded,
    isChargeNow,
    invoiceId,
    trackingContext,
    trackTapToPayPaymentSucceeded,
    trackTapToPayCardSaved,
  ]);

  // Failure — charge-now → payment_failed, save-card → card_save_failed.
  useEffect(() => {
    if (!isFailed || firedRef.current.failed) return;
    firedRef.current.failed = true;
    const failureCode = invoiceData?.data.failureCode ?? null;
    if (isChargeNow) {
      trackTapToPayPaymentFailed({
        page: trackingContext.page,
        invoice_id: invoiceId,
        failure_code: failureCode,
      });
    } else {
      trackTapToPayCardSaveFailed({
        page: trackingContext.page,
        invoice_id: invoiceId,
        failure_code: failureCode,
      });
    }
  }, [
    isFailed,
    isChargeNow,
    invoiceData?.data.failureCode,
    invoiceId,
    trackingContext,
    trackTapToPayPaymentFailed,
    trackTapToPayCardSaveFailed,
  ]);

  // 5-minute reader-polling timeout.
  useEffect(() => {
    if (!isTimedOutPending || firedRef.current.timedOut) return;
    firedRef.current.timedOut = true;
    trackTapToPayPollingTimedOut({
      page: trackingContext.page,
      invoice_id: invoiceId,
    });
  }, [isTimedOutPending, invoiceId, trackingContext, trackTapToPayPollingTimedOut]);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      trapFocus
      centered
      size='md'
      radius='lg'
      withCloseButton={isSucceeded}
      closeOnClickOutside={false}
      closeOnEscape={false}
    >
      <Stack align='center' spacing='lg' p='md'>
        {isInvoiceLoading && (
          <Avatar size='xl' color='cyan' radius='100%'>
            <Loader size='lg' />
          </Avatar>
        )}

        {isTimedOutPending && (
          <>
            <Avatar size='xl' color='orange' radius='100%'>
              <IconCircleX size='2.8rem' />
            </Avatar>
            <Title order={2}>Timed out</Title>
            <Text color='dimmed' ta='center'>
              We didn&apos;t hear back from the reader. Refetch to check status, or cancel and try again.
            </Text>

            <Stack mt='md' spacing='xs' w='100%'>
              <Button
                leftIcon={<IconRotate size={theme.fontSizes.lg} />}
                size='md'
                fullWidth
                color='kiloBlue'
                loading={isInvoiceFetching}
                disabled={cancelTerminalReaderPaymentMutation.isLoading}
                onClick={resetInvoicePoll}
              >
                Keep Waiting
              </Button>
              <Button
                size='md'
                fullWidth
                variant='default'
                onClick={handleCancelTransaction}
                loading={cancelTerminalReaderPaymentMutation.isLoading}
              >
                Cancel Transaction
              </Button>
            </Stack>
          </>
        )}

        {isWaitingForTapToPay && (
          <>
            <Avatar size='xl' color='cyan' radius='100%'>
              <IconCreditCard size='2.8rem' />
            </Avatar>
            <Title order={2}>{isChargeNow ? 'Swipe or tap card to pay' : 'Swipe or tap card to save'}</Title>

            <Button
              mt='md'
              size='md'
              fullWidth
              color='gray'
              variant='subtle'
              onClick={handleCancelTransaction}
              loading={cancelTerminalReaderPaymentMutation.isLoading}
            >
              Cancel Transaction
            </Button>
          </>
        )}
        {isProcessing && (
          <>
            <Avatar size='xl' color='cyan' radius='100%'>
              <Loader size='2.8rem' />
            </Avatar>
            <Title order={2}>{isChargeNow ? 'Processing payment' : 'Saving card'}</Title>

            <Text color='dimmed'>
              {isChargeNow
                ? 'Please wait while we process the payment...'
                : 'Please wait while we save the card...'}
            </Text>
          </>
        )}
        {isSucceeded && (
          <>
            <Avatar size='xl' color='green' radius='100%'>
              <IconCheck size='2.8rem' />
            </Avatar>
            <Title order={2}>{isChargeNow ? 'Payment Successful!' : 'Card Saved!'}</Title>
            <Text color='dimmed'>
              {isChargeNow ? 'The payment has been processed.' : 'The card has been saved for this invoice.'}
            </Text>

            <Button mt='md' size='md' fullWidth color='kiloBlue' onClick={handleClose}>
              {successButtonLabel}
            </Button>
          </>
        )}
        {isFailed && (
          <>
            <Avatar size='xl' color='red' radius='100%'>
              <IconCircleX size='2.8rem' />
            </Avatar>
            <Title order={2}>{isChargeNow ? 'Payment Failed' : 'Card Save Failed'}</Title>
            <Text color='dimmed'>
              {isChargeNow
                ? 'There was an issue processing the payment.'
                : 'There was an issue saving the card.'}
            </Text>
            {invoiceData.data.failureMessage && (
              <Code w='100%' ta='center' p='sm' color='red' c='red.9'>
                {invoiceData.data.failureMessage}
              </Code>
            )}

            <Stack mt='md' spacing='xs' w='100%'>
              <Button
                leftIcon={<IconRotate size={theme.fontSizes.lg} />}
                size='md'
                fullWidth
                color='kiloBlue'
                loading={retryTerminalPaymentMutation.isLoading}
                disabled={cancelTerminalReaderPaymentMutation.isLoading}
                onClick={handleRetryTransaction}
              >
                {isChargeNow ? 'Retry Payment' : 'Retry Save'}
              </Button>
              <Button
                leftIcon={<IconCreditCard size={theme.fontSizes.lg} />}
                size='md'
                fullWidth
                variant='default'
                onClick={handleChangePaymentMethod}
                loading={cancelTerminalReaderPaymentMutation.isLoading}
              >
                Change Payment Method
              </Button>
            </Stack>
          </>
        )}
      </Stack>
    </Modal>
  );
};

export default TapToPayModal;
